/**
 * Admin > Overview: who sees the rave, decided before it starts (the server
 * owner only, see server/src/rave.ts). Everyone with a project open is listed
 * and ticked; anyone who opens one while this is up is listed unticked, so
 * nobody gets a rave you didn't pick.
 */
import { Disc3 } from "lucide-react";
import { useEffect, useState } from "react";
import { Avatar, Modal } from "../components/ui";
import { api, errorMessage } from "../lib/api";
import { toast, useSession } from "../lib/app";
import { startRave, type Rave } from "../project/rave";
import { count } from "./data";
import { Loading } from "./ui";

interface Raver {
  id: number;
  name: string;
  username: string;
  tabs: number;
  projects: string[];
}

/** "Alex", "Alex and Sam", "Alex, Sam and Jo". */
function andList(names: string[]) {
  return names.length <= 1 ? (names[0] ?? "") : `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

export function RaveDialog({ onClose }: { onClose: () => void }) {
  const { user } = useSession();
  const [people, setPeople] = useState<Raver[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [ticked, setTicked] = useState<Set<number>>(new Set());
  /** Who was there when this opened: they start ticked, and anyone who turns up later doesn't. */
  const [first, setFirst] = useState<Set<number> | null>(null);
  const [sound, setSound] = useState(true);
  const [busy, setBusy] = useState(false);

  // Kept fresh while it's open, so the list is who's there now.
  useEffect(() => {
    let alive = true;
    let initial: Set<number> | null = null;
    const load = () =>
      api
        .get<{ people: Raver[] }>("/api/admin/rave")
        .then((r) => {
          if (!alive) return;
          if (!initial) {
            initial = new Set(r.people.map((p) => p.id));
            setFirst(initial);
            setTicked(new Set(initial));
          }
          setPeople(r.people);
          setError(null);
        })
        .catch((e) => alive && setError(errorMessage(e)));
    load();
    const t = window.setInterval(load, 4000);
    return () => {
      alive = false;
      window.clearInterval(t);
    };
  }, []);

  const picked = (people ?? []).filter((p) => ticked.has(p.id));
  const toggle = (id: number) =>
    setTicked((t) => {
      const next = new Set(t);
      if (!next.delete(id)) next.add(id);
      return next;
    });

  function preview() {
    startRave({ id: crypto.randomUUID(), from: { id: user!.id, name: user!.displayName }, sound, guests: picked.map((p) => ({ id: p.id, name: p.name })), preview: true }, user!.id);
  }

  async function start() {
    setBusy(true);
    try {
      const r = await api.post<Rave & { missed: number }>("/api/admin/rave", { userIds: picked.map((p) => p.id), sound });
      onClose();
      startRave(r, user!.id);
      const who = andList(r.guests.map((g) => (g.id === user!.id ? "you" : g.name)));
      toast(r.missed ? `Rave on for ${who}. ${count(r.missed, "person", "people")} left before it started.` : `Rave on for ${who}.`, "success");
    } catch (e) {
      toast(errorMessage(e), "error");
      setBusy(false);
    }
  }

  return (
    <Modal
      title={
        <>
          <Disc3 size={18} /> Start a rave?
        </>
      }
      onClose={onClose}
      width={520}
      footer={
        <>
          <button className="btn" onClick={preview} title="See it on this screen only. Nobody else sees it.">
            Preview on my screen
          </button>
          <span className="grow" />
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={busy || !picked.length} onClick={start}>
            {picked.length ? `Start the rave for ${count(picked.length, "person", "people")}` : "Start the rave"}
          </button>
        </>
      }
    >
      <p className="rave-lede">
        For about 30 seconds their screen goes dark, shakes and flashes on every beat, with a disco ball, lasers, confetti and everyone dancing along the
        bottom. Only the people ticked here see it.
      </p>
      {!people ? (
        error ? (
          <p className="error-text">{error}</p>
        ) : (
          <Loading>Seeing who's here…</Loading>
        )
      ) : people.length === 0 ? (
        <p className="admin-empty">Nobody has a project open right now, so there's nobody to send it to. You can still preview it.</p>
      ) : (
        <ul className="rave-guests">
          {people.map((p) => {
            const isNew = !first?.has(p.id);
            return (
              <li key={p.id}>
                <label className="check">
                  <input type="checkbox" checked={ticked.has(p.id)} onChange={() => toggle(p.id)} />
                  <Avatar id={p.id} name={p.name} size={28} />
                  <span className="grow">
                    <b>{p.id === user?.id ? `${p.name} (you)` : p.name}</b> <span className="muted">@{p.username}</span>
                    <span className="muted small rave-where">
                      {p.projects.length ? `In ${andList(p.projects)}` : "In a project"}
                      {p.tabs > 1 ? ` · ${count(p.tabs, "tab")}` : ""}
                    </span>
                  </span>
                  {isNew && <span className="chip chip-warn">Just arrived</span>}
                </label>
              </li>
            );
          })}
        </ul>
      )}
      <label className="check rave-sound-option">
        <input type="checkbox" checked={sound} onChange={(e) => setSound(e.target.checked)} />
        <span>
          Play the music
          <div className="muted small">A 128 BPM beat with air horns, out of their speakers. Untick it if they might be in a library.</div>
        </span>
      </label>
      <p className="muted small rave-note">
        Nobody can leave early: Esc doesn't stop it, and reloading picks it up again. It flashes about twice a second, and anyone whose computer is set to
        reduce motion gets it without the shaking and flashing.
      </p>
    </Modal>
  );
}
